import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ForumIcon from "@material-ui/icons/Forum";
import { ViewKanban } from "@mui/icons-material";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CachedIcon from "@material-ui/icons/Cached";
import api from "../services/api";
import MainListItems from "./MainListItems";
import IncomingCall from "../components/IncomingCall";
import HorizontalMenu from "./HorizontalMenu";
import ManagerLayout from "./ManagerLayout";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import logo from "../assets/logo1.png";
import logoDark from "../assets/logo2.png";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import usePlans from "../hooks/usePlans";
import VersionControl from "../components/VersionControl";
import SupervisorCompanySwitcher from "../components/SupervisorCompanySwitcher";
import useSocketListener from "../hooks/useSocketListener";
import { FaGlobe } from "react-icons/fa";
import { useHistory, useLocation } from "react-router-dom";

const backendUrl = getBackendUrl();
const drawerWidth = 264;


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.mode === "light" ? "#F3F4F8" : "#111217",
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main, // Usa cor do tema
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "12px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 700,
    },
  },

  ticketsMobileRoot: {
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
    },
  },

  chip: {
    background: "red",
    color: "white",
  },

  avatar: {
    width: "100%",
  },

  toolbar: {
    minHeight: 64,
    padding: "0 24px",
    color: theme.mode === "light" ? "#17181F" : "#F6F7FB",
    background: theme.mode === "light" ? "#FFFFFF" : "#181A22",
    borderBottom: theme.mode === "light" ? "1px solid #E8E9F0" : "1px solid #292C38",
    boxShadow: "none",
    transition: "all 0.2s ease",
  },

  appBarLogo: {
    height: "36px",
    maxWidth: "140px",
    objectFit: "contain",
    marginRight: "12px",
    flexShrink: 0,
    backgroundColor: "transparent",
    mixBlendMode: "normal",
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    minHeight: "64px",
    [theme.breakpoints.down("sm")]: {
      height: "48px",
    },
    // Herda a mesma cor de fundo do drawer — sem fundo branco fixo
    backgroundColor: "transparent",
    borderBottom: theme.mode === "light" ? "1px solid #EAEBF1" : `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  menuButtonHidden: {
    display: "none",
  },

  title: {
    flexGrow: 1,
    fontSize: 14,
    color: theme.mode === "light" ? "#4C5062" : "#E4E6EF",
    fontWeight: 650,
    letterSpacing: "-0.01em",
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    backgroundColor: theme.mode === "light" ? "#FFFFFF" : "#181A22",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "auto",
    // Melhorias sutis no drawer
    borderRight: `1px solid ${theme.mode === "light" ? "#E8E9F0" : "#292C38"}`,
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },

  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "auto",
    backgroundColor: theme.mode === "light" ? "#FFFFFF" : "#181A22",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    ...theme.scrollbarStyles,
  },

  appBarSpacer: {
    minHeight: "64px",
  },

  content: {
    flex: 1,
    overflow: "auto",
    padding: 0,
    margin: 0,
  },

  ticketsMobileContent: {
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
      overflow: "hidden",
    },
  },

  mobileMenuButton: {
    color: theme.mode === "light" ? "#4C5062" : "#F6F7FB",
    flexShrink: 0,
  },

  ticketsMobileMenuButton: {
    position: "fixed",
    top: 66,
    right: 8,
    zIndex: theme.zIndex.appBar + 1,
    color: theme.mode === "light" ? "#4C5062" : "#F6F7FB",
    background: theme.mode === "light" ? "rgba(255,255,255,.94)" : "rgba(24,26,34,.94)",
    boxShadow: "0 2px 12px rgba(0,0,0,.16)",
    "&:hover": {
      background: theme.mode === "light" ? "#FFFFFF" : "#292C38",
    },
  },

  ticketsMobileProfileButton: {
    position: "fixed",
    top: 66,
    right: 54,
    zIndex: theme.zIndex.appBar + 1,
    padding: 4,
    background: theme.mode === "light" ? "rgba(255,255,255,.94)" : "rgba(24,26,34,.94)",
    boxShadow: "0 2px 12px rgba(0,0,0,.16)",
    "&:hover": {
      background: theme.mode === "light" ? "#FFFFFF" : "#292C38",
    },
  },

  mobileDrawer: {
    width: "min(88vw, 340px)",
    backgroundColor: theme.mode === "light" ? "#FFFFFF" : "#181A22",
    color: theme.palette.text.primary,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  container: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "visible",
    overflowX: "visible",
    ...theme.scrollbarStyles,
    borderRadius: "16px",
    border: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
  },

  NotificationsPopOver: {
    // Mantém original
  },

  logo: {
    width: "100%",
    height: "45px",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    logo: theme.logo,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
    transition: "all 0.3s ease",
    backgroundColor: "transparent",
    mixBlendMode: theme.mode === "dark" ? "screen" : "normal",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },

  hideLogo: {
    display: "none",
  },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      borderColor: theme.palette.primary.main, // Usa cor do tema
    },
  },

  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  // Botões da toolbar melhorados
  toolbarButton: {
    color: theme.mode === "light" ? "#55596C" : "#E4E6EF",
    borderRadius: "10px",
    padding: "8px",
    margin: "0 2px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#F0F1FF" : "rgba(255,255,255,.08)",
      color: "#4450FF",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  // Menu hambúrguer com animação sutil
  menuButton: {
    color: theme.mode === "light" ? "#17181F" : "#FFFFFF",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#F0F1FF" : "rgba(255,255,255,.08)",
    },
    "& .MuiSvgIcon-root": {
      transition: "transform 0.3s ease",
    },
    "&:hover .MuiSvgIcon-root": {
      transform: "rotate(90deg)",
    },
  },

  // Seletor de idioma melhorado
  languageSelector: {
    position: "relative",
    display: "inline-block",
    "& > button": {
      background: "rgba(255, 255, 255, 0.1)",
      border: "none",
      borderRadius: "4px",
      color: "rgba(255, 255, 255, 0.9)",
      fontSize: "18px",
      padding: "8px 12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "rgba(255, 255, 255, 0.2)",
        transform: "translateY(-1px)",
      },
    },
    "& > div": {
      position: "absolute",
      top: "45px",
      left: "0",
      background: "#fff",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      borderRadius: "4px",
      padding: "8px",
      zIndex: 1000,
      minWidth: "120px",
      "& button": {
        background: "none",
        border: "none",
        color: "#374151",
        display: "block",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.2s ease",
        "&:hover": {
          background: `${theme.palette.primary.main}10`, // Usa cor do tema
          color: theme.palette.primary.main, // Usa cor do tema
          transform: "none",
        },
      },
    },
  },

  // Badge animado
  animatedBadge: {
    "& .MuiBadge-badge": {
      animation: "$heartbeat 2s infinite",
    },
  },

  "@keyframes heartbeat": {
    "0%": { transform: "scale(1)" },
    "14%": { transform: "scale(1.1)" },
    "28%": { transform: "scale(1)" },
    "42%": { transform: "scale(1.1)" },
    "70%": { transform: "scale(1)" },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const isTicketsRoute = location.pathname.startsWith("/tickets");
  const isTicketDetailRoute = isTicketsRoute && location.pathname.split("/").filter(Boolean).length > 1;
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(() => window.innerWidth > 960);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [menuStyle, setMenuStyle] = useState(() => localStorage.getItem("menuStyle") || "vertical");

  useEffect(() => {
    const handleMenuStyleChanged = () => {
      setMenuStyle(localStorage.getItem("menuStyle") || "vertical");
    };
    window.addEventListener("menuStyleChanged", handleMenuStyleChanged);
    return () => window.removeEventListener("menuStyleChanged", handleMenuStyleChanged);
  }, []);

  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    try {
      const stored = localStorage.getItem("announcementsDismissed");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { getPlanCompany } = usePlans();
  const [mobileCanUseInternalChat, setMobileCanUseInternalChat] = useState(false);
  const [mobileCanUseKanban, setMobileCanUseKanban] = useState(false);

  useEffect(() => {
    if (!isMobile || !user?.companyId) return undefined;
    let active = true;
    setMobileCanUseInternalChat(false);
    setMobileCanUseKanban(false);

    getPlanCompany(undefined, user.companyId)
      .then((configs) => {
        if (!active) return;
        const plan = configs?.plan || {};
        setMobileCanUseInternalChat(Boolean(user.super || plan.useInternalChat));
        setMobileCanUseKanban(Boolean(user.super || plan.useKanban));
      })
      .catch(() => {
        if (!active) return;
        setMobileCanUseInternalChat(Boolean(user.super));
        setMobileCanUseKanban(Boolean(user.super));
      });

    return () => { active = false; };
    // getPlanCompany is recreated by the hook on each render; company and role define this request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, user?.companyId, user?.super]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const navigateMobile = (path) => {
    setMobileNavOpen(false);
    history.push(path);
  };

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  const handleDismissAnnouncement = (ann) => {
    const updatedAtKey = ann.updatedAt || ann.createdAt;
    setDismissedAnnouncements(prev => {
      const updated = { ...prev, [ann.id]: updatedAtKey };
      try { localStorage.setItem("announcementsDismissed", JSON.stringify(updated)); } catch { }
      return updated;
    });
    // Remove da lista visível
    setAnnouncements(prev => prev.filter(a => a.id !== ann.id));
    setSelectedAnnouncement(null);
    // Se não sobrar nenhum, fecha o modal
    const remaining = announcements.filter(a => a.id !== ann.id);
    if (remaining.length === 0) {
      setShowAnnouncementsModal(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncements = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        setAnnouncements(activeAnnouncements);

        // Filtra informativos não dispensados
        let storedDismissed = {};
        try {
          const stored = localStorage.getItem("announcementsDismissed");
          storedDismissed = stored ? JSON.parse(stored) : {};
        } catch { }

        const visibleAnnouncements = activeAnnouncements.filter(ann => {
          const dismissedAt = storedDismissed[ann.id];
          if (!dismissedAt) return true;
          return ann.updatedAt && ann.updatedAt !== dismissedAt;
        });

        // Mostra o modal apenas se houver informativos visíveis
        if (visibleAnnouncements.length > 0) {
          setShowAnnouncementsModal(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id]);

  useEffect(() => {
    // if (localStorage.getItem("public-token") === null) {
    //   handleLogout()
    // }

    setDrawerOpen(!isMobile && !isTicketsRoute && user.defaultMenu !== "closed");
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, isMobile, isTicketsRoute]);

  useEffect(() => {
    if (isMobile) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [isMobile]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de usuário recebido:", data);
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de contato recebido:", data);
    // Para contatos, apenas logamos por enquanto
    // A mensagem já foi enviada automaticamente pelo backend
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          console.log("🎂 Usuário faz aniversário hoje! Mostrando modal...");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          console.log("🎂 Há aniversariantes hoje:", birthdayData);
        }
      } catch (error) {
        console.error("Erro ao verificar aniversários:", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600 || user.defaultMenu === "closed") {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português", flag: "🇧🇷" },
    { code: "en", label: "English", flag: "🇺🇸" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "ar", label: "عربي", flag: "🇸🇦" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={clsx(classes.root, "logged-in-layout", isTicketsRoute && classes.ticketsMobileRoot)}>
      {!hideMenu && !isMobile && menuStyle === "vertical" && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            ),
          }}
          open={drawerOpen}
        >
          <div className={classes.toolbarIcon}>
            <img
              className={drawerOpen ? classes.logo : classes.hideLogo}
              src={
                theme.mode === "dark"
                  ? theme.calculatedLogoDark()
                  : theme.calculatedLogoLight()
              }
              style={{
                display: "block",
                margin: "0 auto",
                height: "50px",
                width: "100%",
                backgroundColor: "transparent",
                objectFit: "contain",
                // screen: fundo branco da logo some em fundo escuro (branco×escuro = transparente visualmente)
                // normal: mantém cores reais no modo claro
                mixBlendMode: theme.mode === "dark" ? "screen" : "normal",
              }}
              alt="logo"
            />
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <List className={classes.containerWithScroll}>
            {/* {mainListItems} */}
            <MainListItems collapsed={!drawerOpen} />
          </List>
          <Divider />
        </Drawer>
      )}

      {(!isTicketsRoute || greaterThenSm || (isMobile && !isTicketDetailRoute)) && <AppBar
        position="absolute"
        className={clsx(classes.appBar, !hideMenu && menuStyle === "vertical" && drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {!hideMenu && menuStyle === "vertical" && !isMobile && (
            <IconButton
              edge="start"
              variant="contained"
              aria-label="open drawer"
              style={{ color: "white" }}
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={clsx(drawerOpen && classes.menuButtonHidden)}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo no canto esquerdo quando menu horizontal */}
          {!hideMenu && menuStyle === "horizontal" && !isMobile && (
            <img
              className={classes.appBarLogo}
              src={
                (colorMode.mode === "dark"
                  ? colorMode.appLogoHorizontalMenuDark || colorMode.appLogoHorizontalMenu
                  : colorMode.appLogoHorizontalMenu) ||
                (colorMode.mode === "dark"
                  ? theme.calculatedLogoDark()
                  : theme.calculatedLogoLight())
              }
              alt="logo"
            />
          )}

          {/* Menu horizontal inline no AppBar */}
          {!hideMenu && menuStyle === "horizontal" && !isMobile && (
            <HorizontalMenu />
          )}

          {!hideMenu && isMobile && (
            <IconButton
              aria-label="Abrir menu de navegação"
              aria-controls="mobile-navigation-drawer"
              aria-expanded={mobileNavOpen}
              className={classes.mobileMenuButton}
              onClick={() => setMobileNavOpen(true)}
              edge="start"
            >
              <MoreVertIcon />
            </IconButton>
          )}

          {!(isMobile && isTicketsRoute) && <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            {/* {greaterThenSm && user?.profile === "admin" && getDateAndDifDays(user?.company?.dueDate).difData < 7 ? ( */}
            {greaterThenSm &&
              user?.profile === "admin" &&
              user?.company?.dueDate ? (
              <>
                {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                {i18n.t("mainDrawer.appBar.user.messageEnd")}{" "}
                <b>{user?.company?.name}</b>! (
                {i18n.t("mainDrawer.appBar.user.active")}{" "}
                {dateToClient(user?.company?.dueDate)})
              </>
            ) : (
              <>
                {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                {i18n.t("mainDrawer.appBar.user.messageEnd")}{" "}
                <b>{user?.company?.name}</b>!
              </>
            )}
          </Typography>}

          {!hideMenu && !(isMobile && isTicketsRoute) && (
            <>
              <SupervisorCompanySwitcher />

              <VersionControl
                onUpdateStart={handleUpdateStart}
                onUpdateComplete={handleUpdateComplete}
              />

              <div
                style={{ position: "relative", display: "inline-block" }}
                className="language-dropdown"
              >
                <button
                  onClick={() => setShowOptions(!showOptions)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "22px",
                    paddingRight: "20px",
                    paddingTop: "8px",
                    lineHeight: 1,
                  }}
                  title={LANGUAGE_OPTIONS.find(l => l.code === i18n.language || l.code === localStorage.getItem("language"))?.label || "Idioma"}
                >
                  <span style={{ fontSize: "20px" }}>
                    {LANGUAGE_OPTIONS.find(l => l.code === i18n.language || l.code === localStorage.getItem("language"))?.flag || <FaGlobe />}
                  </span>
                </button>

                {showOptions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "35px",
                      left: "0",
                      background: "#fff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      borderRadius: "4px",
                      padding: "8px",
                      zIndex: 1000,
                      minWidth: "120px",
                      maxWidth: "200px",
                    }}
                  >
                    {filteredLanguageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          fontSize: "14px",
                          color: "#273142",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                      >
                        <span style={{ fontSize: "20px", lineHeight: 1 }}>{lang.flag}</span>
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <NotificationsVolume setVolume={setVolume} volume={volume} />

              <IconButton
                onClick={handleRefreshPage}
                aria-label={i18n.t("mainDrawer.appBar.refresh")}
                color="inherit"
              >
                <CachedIcon style={{ color: "white" }} />
              </IconButton>

              {/* <DarkMode themeToggle={themeToggle} /> */}

              {user.id && <NotificationsPopOver volume={volume} />}

              <AnnouncementsPopover />

              <ChatPopover />



              <div className="user-menu-wrapper">
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  variant="dot"
                  onClick={handleMenu}
                >
                  <Avatar
                    alt="BotConnecta"
                    className={classes.avatar2}
                    src={profileUrl}
                  />
                </StyledBadge>

                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                  userId={user?.id}
                />

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  PaperProps={{
                    style: {
                      minWidth: "150px",
                      maxWidth: "200px",
                      width: "auto",
                    },
                  }}
                >
                  <MenuItem onClick={handleOpenUserModal}>
                    {i18n.t("mainDrawer.appBar.user.profile")}
                  </MenuItem>
                  <MenuItem onClick={colorMode.toggleColorMode}>
                    {theme.mode === "dark" ? i18n.t("mainDrawer.appBar.user.themeLight") : i18n.t("mainDrawer.appBar.user.themeDark")}
                  </MenuItem>
                  <MenuItem onClick={handleClickLogout}>
                    {i18n.t("mainDrawer.appBar.user.logout")}
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}

          {!hideMenu && isMobile && isTicketsRoute && (
            <>
              <div style={{ flexGrow: 1 }} />
              <IconButton
                aria-label={i18n.t("mainDrawer.appBar.user.profile")}
                aria-controls="menu-profile-mobile-ticket-list"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={handleMenu}
                edge="end"
              >
                <Avatar alt={user?.name || "Perfil"} className={classes.avatar2} src={profileUrl} />
              </IconButton>
              <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                userId={user?.id}
              />
              <Menu
                id="menu-profile-mobile-ticket-list"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={menuOpen}
                onClose={handleCloseMenu}
                PaperProps={{ style: { minWidth: 150, maxWidth: 200 } }}
              >
                <MenuItem onClick={handleOpenUserModal}>
                  {i18n.t("mainDrawer.appBar.user.profile")}
                </MenuItem>
                <MenuItem onClick={colorMode.toggleColorMode}>
                  {theme.mode === "dark" ? i18n.t("mainDrawer.appBar.user.themeLight") : i18n.t("mainDrawer.appBar.user.themeDark")}
                </MenuItem>
                <MenuItem onClick={handleClickLogout}>
                  {i18n.t("mainDrawer.appBar.user.logout")}
                </MenuItem>
              </Menu>
            </>
          )}

        </Toolbar>
      </AppBar>}
      {!hideMenu && isMobile && isTicketDetailRoute && (
        <>
          <IconButton
            aria-label="Abrir menu de navegação"
            aria-controls="mobile-navigation-drawer"
            aria-expanded={mobileNavOpen}
            className={classes.ticketsMobileMenuButton}
            onClick={() => setMobileNavOpen(true)}
          >
            <MoreVertIcon />
          </IconButton>
          <IconButton
            aria-label={i18n.t("mainDrawer.appBar.user.profile")}
            aria-controls="menu-profile-mobile-tickets"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className={classes.ticketsMobileProfileButton}
            onClick={handleMenu}
          >
            <Avatar alt={user?.name || "Perfil"} className={classes.avatar2} src={profileUrl} />
          </IconButton>
          <UserModal
            open={userModalOpen}
            onClose={() => setUserModalOpen(false)}
            onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
            userId={user?.id}
          />
          <Menu
            id="menu-profile-mobile-tickets"
            anchorEl={anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={menuOpen}
            onClose={handleCloseMenu}
            PaperProps={{ style: { minWidth: 150, maxWidth: 200 } }}
          >
            <MenuItem onClick={handleOpenUserModal}>
              {i18n.t("mainDrawer.appBar.user.profile")}
            </MenuItem>
            <MenuItem onClick={colorMode.toggleColorMode}>
              {theme.mode === "dark" ? i18n.t("mainDrawer.appBar.user.themeLight") : i18n.t("mainDrawer.appBar.user.themeDark")}
            </MenuItem>
            <MenuItem onClick={handleClickLogout}>
              {i18n.t("mainDrawer.appBar.user.logout")}
            </MenuItem>
          </Menu>
        </>
      )}
      {!hideMenu && isMobile && (
        <Drawer
          id="mobile-navigation-drawer"
          anchor="right"
          variant="temporary"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          classes={{ paper: classes.mobileDrawer }}
          ModalProps={{ keepMounted: true }}
        >
          <div style={{ padding: "22px 20px 12px" }}>
            <Typography variant="overline" color="textSecondary" style={{ letterSpacing: ".12em", fontWeight: 700 }}>
              Navegação
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.25 }}>
              {user?.company?.name || "Zappro"}
            </Typography>
          </div>
          <Divider />
          {user?.profile === "admin" ? (
            <List style={{ padding: "10px 12px 20px" }}>
              <MainListItems collapsed={false} drawerClose={() => setMobileNavOpen(false)} />
            </List>
          ) : (
            <List style={{ padding: "10px 12px 20px" }}>
              {[
                ...(mobileCanUseInternalChat ? [{ path: "/chats", label: i18n.t("mainDrawer.listItems.chats"), icon: <ForumIcon /> }] : []),
                { path: "/tickets", label: i18n.t("mainDrawer.listItems.tickets"), icon: <WhatsAppIcon /> },
                ...(mobileCanUseKanban ? [{ path: "/kanban", label: i18n.t("mainDrawer.listItems.kanban"), icon: <ViewKanban /> }] : []),
              ].map((item) => (
                <ListItem
                  button
                  key={item.path}
                  selected={location.pathname.startsWith(item.path)}
                  onClick={() => navigateMobile(item.path)}
                  style={{ minHeight: 52, borderRadius: 12, marginBottom: 4 }}
                >
                  <ListItemIcon style={{ color: theme.palette.primary.main, minWidth: 42 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ style: { fontWeight: 600 } }} />
                </ListItem>
              ))}
            </List>
          )}
        </Drawer>
      )}
      <main className={clsx(classes.content, isTicketsRoute && classes.ticketsMobileContent)}>
        {(!isTicketsRoute || greaterThenSm || (isMobile && !isTicketDetailRoute)) && <div className={classes.appBarSpacer} />}
        {children ? children : null}
        <IncomingCall />
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  onClick={() => handleDismissAnnouncement(selectedAnnouncement)}
                  color="default"
                  size="small"
                  style={{ fontSize: 12, textTransform: "none", color: "#888" }}
                >
                  Não exibir novamente
                </Button>
                <Button
                  onClick={() => setSelectedAnnouncement(null)}
                  variant="outlined"
                >
                  Voltar para lista
                </Button>
              </div>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

    </div>
  );
};

function RoleLayout(props) {
  const { user, loading, isAuth } = useContext(AuthContext);
  if (loading) return <BackdropLoading />;
  if (!isAuth) return <>{props.children}</>;
  return user?.profile === "manager" ? <ManagerLayout {...props} /> : <LoggedInLayout {...props} />;
}

export default RoleLayout;
